/**
 * SPA entry point.
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'

import { router } from '@/routes'
import { AuthProvider } from '@/features/auth/authContext'
import { ThemeProvider } from '@/features/theme/ThemeProvider'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ToastHost } from '@/features/feedback/toast'

import '@/styles/index.css'
// The OG Friday comic layer comes last on purpose: it has to sit after
// Tailwind's components layer to win the cascade, and it has to live in its
// own file so `@apply btn` does not rewrite its rules.
import '@/features/theme/og-friday.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Re-fetch when the user returns to the tab; almost every mutation
      // in Friday should be reflected on focus.
      refetchOnWindowFocus: 'always',
      staleTime: 30_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status
        if (status && status >= 400 && status < 500) return false
        return failureCount < 2
      },
    },
    mutations: {
      retry: 0,
    },
  },
})

const container = document.getElementById('root')
if (!container) throw new Error('Missing #root')

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ErrorBoundary>
            <RouterProvider router={router} />
          </ErrorBoundary>
          <ToastHost />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
)
